import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { confirmDialog } from '../lib/confirmDialog';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import PortalPreview from '../components/portal-builder/PortalPreview';
import SectionEditor from '../components/portal-builder/SectionEditor';

/**
 * PortalBuilder - WYSIWYG portal page builder
 * Allows team managers to customize their public portal layout, content visibility, and sections
 * Paid tier only
 */
export default function PortalBuilder() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teamId, setTeamId] = useState(null);
  const [teamInfo, setTeamInfo] = useState(null);
  
  // Portal configuration
  const [sections, setSections] = useState([]);
  const [pendingDeletes, setPendingDeletes] = useState([]);
  const [portalData, setPortalData] = useState(null);
  
  // Track changes
  const [hasChanges, setHasChanges] = useState(false);
  
  // UI state
  const [activeSection, setActiveSection] = useState(null);
  const [mobileView, setMobileView] = useState('editor'); // 'editor' or 'preview'
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please sign in to edit the portal');
        navigate('/login');
        return;
      }

      // Get team info and check paid tier
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .select('id, name, season_tier, url_slug')
        .eq('user_id', user.id)
        .single();

      if (teamError || !team) {
        console.error('Error fetching team:', teamError);
        toast.error('No team found');
        navigate('/dashboard');
        return;
      }

      const tier = team.season_tier;
      if (tier !== 'paid') {
        toast.error('Portal editing requires paid tier');
        navigate('/dashboard');
        return;
      }

      setTeamId(team.id);
      setTeamInfo(team);
      await loadPortalConfig(team.id, team.url_slug);
    } catch (error) {
      console.error('Error checking access:', error);
      toast.error('Error loading portal builder');
      navigate('/dashboard');
    }
  };

  const loadPortalConfig = async (tid, slug) => {
    try {
      setLoading(true);
      
      // Load portal sections configuration
      const { data: sectionsData, error: sectionsError } = await supabase
        .from('portal_sections')
        .select('*')
        .eq('team_id', tid)
        .order('sort_order', { ascending: true });

      if (sectionsError) throw sectionsError;

      // Load all portal data using v2 RPC
      const { data: portalDataResult, error: portalError } = await supabase
        .rpc('get_portal_data_v2', { slug_param: slug });

      if (portalError) throw portalError;

      const normalizedSections = (sectionsData || []).map(section => ({
        ...section,
        _status: 'clean'
      }));

      setSections(normalizedSections);
      setPendingDeletes([]);
      setPortalData(portalDataResult);
      setLoading(false);
    } catch (error) {
      console.error('Error loading portal config:', error);
      toast.error('Error loading portal configuration');
      setLoading(false);
    }
  };

  const handleSectionUpdate = (sectionId, updates) => {
    setSections(prev => prev.map(s => {
      if (s.id === sectionId) {
        const updatedSection = updates.section_config
          ? {
              ...s,
              ...updates,
              section_config: {
                ...(s.section_config || {}),
                ...updates.section_config
              }
            }
          : { ...s, ...updates };

        return {
          ...updatedSection,
          _status: s._status === 'new' ? 'new' : 'dirty'
        };
      }
      return s;
    }));
    setHasChanges(true);
  };

  const handleCollectionUpdate = (collectionKey, sectionId, updatedItems) => {
    setPortalData(prev => {
      if (!prev) return prev;

      const remaining = (prev[collectionKey] || []).filter(item => item.section_id !== sectionId);
      return {
        ...prev,
        [collectionKey]: [...remaining, ...updatedItems]
      };
    });
  };

  const handleSectionReorder = (dragIndex, hoverIndex) => {
    // Validate indices
    if (dragIndex < 0 || dragIndex >= sections.length) return;
    if (hoverIndex < 0 || hoverIndex >= sections.length) return;
    if (dragIndex === hoverIndex) return;

    const updated = [...sections];
    const [removed] = updated.splice(dragIndex, 1);
    updated.splice(hoverIndex, 0, removed);

    const reordered = updated.map((section, idx) => ({
      ...section,
      sort_order: idx,
      _status: section._status === 'new' ? 'new' : 'dirty'
    }));

    setSections(reordered);
    setHasChanges(true);
  };

  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const fromIndex = sections.findIndex((section) => section.id === active.id);
    const toIndex = sections.findIndex((section) => section.id === over.id);
    handleSectionReorder(fromIndex, toIndex);
  };

  const handleAddSection = async (sectionType, options = {}) => {
    if (!teamId) return;

    const sectionConfig = options.sectionConfig ?? getInitialSectionConfig(sectionType);
    const title = options.title ?? getSectionDefaultTitle(sectionType);

    const insertPayload = {
      team_id: teamId,
      section_type: sectionType,
      title,
      visible: true,
      sort_order: sections.length,
      section_config: sectionConfig
    };

    try {
      const { data, error } = await supabase
        .from('portal_sections')
        .insert(insertPayload)
        .select()
        .single();

      if (error) throw error;

      if (data) {
        setSections(prev => [
          ...prev,
          {
            ...data,
            section_config: sectionConfig,
            _status: 'clean'
          }
        ]);
        setActiveSection(data.id);
        return data;
      }
    } catch (error) {
      console.error('Error adding section:', error);

      // Fallback error handling (should rarely occur now that we prevent duplicates in UI)
      if (error.code === '23505' && error.message.includes('unique_non_custom_sections')) {
        const sectionTypeName = getSectionTypeDisplayName(sectionType);
        toast.error(`You can only have one ${sectionTypeName} per team. Try adding a Custom Section instead.`);
      } else {
        toast.error('Failed to add section: ' + error.message);
      }
    }
    return null;
  };

  const handleDeleteSection = async (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    const confirmed = await confirmDialog(
      `Delete "${section.title}" section?`,
      { confirmText: 'Delete', cancelText: 'Cancel' }
    );
    
    if (!confirmed) return;

    setSections(prev => prev.filter(s => s.id !== sectionId));
    setPendingDeletes(prev =>
      prev.includes(sectionId) ? prev : [...prev, sectionId]
    );
    setHasChanges(true);
    toast.success('Section will be deleted when you save');
  };

  const handleSaveAll = async () => {
    if (!hasChanges) {
      toast.info('No changes to save');
      return;
    }

    setSaving(true);
    try {
      const sectionsToUpdate = sections.filter(section => section._status === 'dirty');

      for (const section of sectionsToUpdate) {
        const { error } = await supabase
          .from('portal_sections')
          .update({
            title: section.title,
            visible: section.visible,
            sort_order: section.sort_order,
            section_config: section.section_config || {}
          })
          .eq('id', section.id);

        if (error) {
          console.error('[PortalBuilder] Update error:', error);
          throw error;
        }
      }

      for (const sectionId of pendingDeletes) {
        const { error } = await supabase
          .from('portal_sections')
          .delete()
          .eq('id', sectionId);

        if (error) {
          console.error('[PortalBuilder] Delete error:', error);
          throw error;
        }
      }

      toast.success('Portal saved successfully!');
      setHasChanges(false);
      setPendingDeletes([]);
      
      // Reload config to get fresh IDs
      await loadPortalConfig(teamId, teamInfo.url_slug);
    } catch (error) {
      console.error('Error saving portal:', error);
      toast.error('Error saving portal: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (hasChanges) {
      const confirmed = await confirmDialog(
        'You have unsaved changes. Are you sure you want to leave?',
        { confirmText: 'Leave', cancelText: 'Stay' }
      );
      if (!confirmed) return;
    }
    navigate('/dashboard');
  };

  const getSectionDefaultTitle = (type) => {
    const defaults = {
      dispatches: 'Recent Updates',
      wiki: 'Quick Info',
      calendar: 'Upcoming Events',
      roster: 'Team Roster',
      files: 'Files & Documents',
      snacks: 'Snack Schedule',
      custom: 'Custom Section',
      links: 'Important Links',
      gallery: 'Photo Gallery',
      videos: 'Video Highlights',
      attachments: 'Downloads',
      contact_form: 'Contact Us',
      event_calendar: 'Full Schedule',
      social_feed: 'Social Feed'
    };
    return defaults[type] || 'New Section';
  };

  const getSectionTypeDisplayName = (type) => {
    const displayNames = {
      dispatches: 'Updates Section',
      wiki: 'Info Section',
      calendar: 'Events Section',
      roster: 'Roster Section',
      files: 'Files Section',
      snacks: 'Snack Section',
      custom: 'Custom Section',
      links: 'Links Section',
      gallery: 'Photo Gallery',
      videos: 'Video Highlights',
      attachments: 'Downloads Section',
      contact_form: 'Contact Form',
      event_calendar: 'Event Calendar',
      social_feed: 'Social Feed'
    };
    return displayNames[type] || 'Section';
  };

  const getInitialSectionConfig = (type) => {
    switch (type) {
      case 'contact_form':
        return {
          contactForm: {
            recipients: [],
            successMessage: 'Thanks for reaching out! We will get back to you soon.',
            collectPhone: true
          }
        };
      case 'event_calendar':
        return {
          eventCalendar: {
            weeksAhead: 4,
            includePastWeeks: 0,
            groupBy: 'month',
            showLocations: true
          }
        };
      default:
        return {};
    }
  };

  // Check which section types already exist (except custom which allows multiples)
  const existingSectionTypes = sections
    .filter(section => section.section_type !== 'custom')
    .map(section => section.section_type);

  const sectionOptions = [
    { type: 'links', label: 'Links Section', description: 'List important websites and resources.' },
    { type: 'custom', label: 'Custom Section', description: 'Rich text content with images and formatting.' },
    { type: 'gallery', label: 'Photo Gallery', description: 'Upload images to showcase your team.' },
    { type: 'videos', label: 'Video Highlights', description: 'Embed YouTube or Vimeo clips.' },
    { type: 'attachments', label: 'Downloads', description: 'Share PDFs, forms, and handouts.' },
    { type: 'contact_form', label: 'Contact Form', description: 'Collect inquiries from families.' },
    { type: 'event_calendar', label: 'Event Calendar', description: 'Advanced schedule layout with grouping.' },
    { type: 'social_feed', label: 'Social Feed', description: 'Embed social media posts or profile links.' }
  ];

  const portalTemplates = [
    {
      id: 'game-day-hub',
      label: 'Game Day Hub',
      description: 'Schedule, resources, and a quick question form for game days.',
      sections: [
        {
          type: 'event_calendar',
          title: 'Game Day Schedule',
          sectionConfig: {
            eventCalendar: {
              weeksAhead: 2,
              includePastWeeks: 1,
              groupBy: 'day',
              showLocations: true
            }
          }
        },
        {
          type: 'attachments',
          title: 'Game Day Documents'
        },
        {
          type: 'contact_form',
          title: 'Questions for the Bench',
          sectionConfig: {
            contactForm: {
              recipients: [],
              successMessage: 'Thanks! We will respond before the puck drops.',
              collectPhone: true
            }
          }
        }
      ]
    },
    {
      id: 'travel-weekend',
      label: 'Travel Weekend Pack',
      description: 'Ready-to-go travel instructions with links and downloads.',
      sections: [
        {
          type: 'custom',
          title: 'Travel Weekend Overview',
          sectionConfig: {
            custom_html: '<h2>Travel Weekend Details</h2><p>Review hotel check-in times, bus departure, and uniform reminders here.</p>'
          }
        },
        {
          type: 'links',
          title: 'Hotel & Venue Links'
        },
        {
          type: 'attachments',
          title: 'Travel Documents'
        }
      ]
    },
    {
      id: 'season-starter',
      label: 'Season Starter Kit',
      description: 'Welcome message, highlight reel, and contact form for new families.',
      sections: [
        {
          type: 'custom',
          title: 'Welcome to the Season',
          sectionConfig: {
            custom_html: '<h2>Welcome!</h2><p>We are excited to kick off an amazing season. Stay tuned for weekly updates.</p>'
          }
        },
        {
          type: 'videos',
          title: 'Highlight Reel'
        },
        {
          type: 'contact_form',
          title: 'Manager Contact',
          sectionConfig: {
            contactForm: {
              recipients: [],
              successMessage: 'Thanks for saying hello! We will respond shortly.',
              collectPhone: false
            }
          }
        }
      ]
    }
  ];

  const applyTemplate = async (template) => {
    if (!template?.sections?.length) return;

    setShowTemplateMenu(false);

    for (const definition of template.sections) {
      await handleAddSection(definition.type, {
        title: definition.title,
        sectionConfig: definition.sectionConfig
      });
    }

    toast.success(`${template.label} template added to your portal`);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-full mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">Portal Builder</h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1 truncate">
                {teamInfo?.name} - Customize your public portal
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={handleCancel}
                className="px-3 sm:px-4 py-2 text-sm sm:text-base text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAll}
                disabled={!hasChanges || saving}
                className={`px-3 sm:px-4 py-2 text-sm sm:text-base text-white rounded-lg font-medium ${
                  hasChanges && !saving
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'No Changes'}
              </button>
            </div>
          </div>

          {hasChanges && (
            <div className="mt-2 sm:mt-3 bg-amber-50 border border-amber-200 rounded-lg p-2 sm:p-3 text-xs sm:text-sm text-amber-800">
              You have unsaved changes. Click "Save Changes" to publish your edits.
            </div>
          )}
        </div>
      </div>

      {/* Mobile Toggle Tabs - Only visible on mobile/tablet */}
      <div className="lg:hidden bg-white border-b border-gray-200 sticky top-[120px] sm:top-[130px] z-40">
        <div className="flex">
          <button
            onClick={() => setMobileView('editor')}
            className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
              mobileView === 'editor'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Edit Sections
          </button>
          <button
            onClick={() => setMobileView('preview')}
            className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${
              mobileView === 'preview'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Preview Portal
          </button>
        </div>
      </div>

      {/* Main Content - Responsive Layout */}
      <div className="lg:flex lg:h-[calc(100vh-140px)]">
        {/* Left Panel - Section Editor */}
        <div className={`lg:w-2/5 lg:border-r border-gray-200 bg-white overflow-y-auto ${
          mobileView === 'preview' ? 'hidden lg:block' : 'block'
        }`}>
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Portal Sections</h2>
              <div className="flex gap-3">
                <div className="relative">
                  <button
                    onClick={() => {
                      setShowTemplateMenu(false);
                      setShowAddMenu((prev) => !prev);
                    }}
                    className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap flex items-center gap-1"
                  >
                    + Add Section
                    <span className="text-[10px]">{showAddMenu ? '▲' : '▼'}</span>
                  </button>
                  {showAddMenu && (
                    <div
                      className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20"
                      onMouseLeave={() => setShowAddMenu(false)}
                    >
                      <ul className="py-2 max-h-72 overflow-y-auto">
                        {sectionOptions.map((option) => {
                          const isAlreadyAdded = existingSectionTypes.includes(option.type);
                          const existingSection = isAlreadyAdded ? sections.find(s => s.section_type === option.type) : null;

                          return (
                            <li key={option.type}>
                              <button
                                onClick={() => {
                                  setShowAddMenu(false);
                                  if (isAlreadyAdded && existingSection) {
                                    // Navigate to existing section instead of showing error
                                    setActiveSection(existingSection.id);
                                  } else {
                                    handleAddSection(option.type);
                                  }
                                }}
                                className={`w-full text-left px-3 py-2 relative ${
                                  isAlreadyAdded
                                    ? 'bg-green-50 hover:bg-green-100 cursor-pointer'
                                    : 'hover:bg-blue-50 cursor-pointer'
                                }`}
                                disabled={false} // Allow clicking even for existing sections
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <p className={`text-sm font-medium ${
                                      isAlreadyAdded ? 'text-green-800' : 'text-gray-900'
                                    }`}>
                                      {option.label}
                                    </p>
                                    <p className={`text-xs ${
                                      isAlreadyAdded ? 'text-green-600' : 'text-gray-600'
                                    }`}>
                                      {isAlreadyAdded ? 'Already added - click to edit' : option.description}
                                    </p>
                                  </div>
                                  {isAlreadyAdded && (
                                    <div className="ml-2 flex-shrink-0">
                                      <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                                        <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button
                    onClick={() => {
                      setShowAddMenu(false);
                      setShowTemplateMenu((prev) => !prev);
                    }}
                    className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap flex items-center gap-1"
                  >
                    Templates
                    <span className="text-[10px]">{showTemplateMenu ? '▲' : '▼'}</span>
                  </button>
                  {showTemplateMenu && (
                    <div
                      className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-20"
                      onMouseLeave={() => setShowTemplateMenu(false)}
                    >
                      <ul className="py-2 space-y-1 max-h-80 overflow-y-auto">
                        {portalTemplates.map((template) => (
                          <li key={template.id}>
                            <button
                              onClick={() => applyTemplate(template)}
                              className="w-full text-left px-3 py-2 hover:bg-blue-50"
                            >
                              <p className="text-sm font-semibold text-gray-900">{template.label}</p>
                              <p className="text-xs text-gray-600">{template.description}</p>
                              <p className="text-[11px] text-gray-400 mt-1">
                                Adds {template.sections.length} section{template.sections.length > 1 ? 's' : ''}
                              </p>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DndContext collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
              <SortableContext
                items={sections.map((section) => section.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {sections.map((section) => (
                    <SectionEditor
                      key={section.id}
                      section={section}
                      portalData={portalData}
                      teamId={teamId}
                      allSections={sections}
                      onUpdate={(updates) => handleSectionUpdate(section.id, updates)}
                      onDelete={() => handleDeleteSection(section.id)}
                      onSettingsUpdate={loadPortalConfig}
                      onCollectionUpdate={handleCollectionUpdate}
                      isActive={activeSection === section.id}
                      onActivate={() => setActiveSection(section.id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {sections.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p>No sections configured yet</p>
                <button
                  onClick={() => handleAddSection('custom')}
                  className="mt-2 text-blue-600 hover:underline"
                >
                  Add your first section
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Live WYSIWYG Preview */}
        <div className={`lg:flex-1 bg-gray-100 overflow-y-auto ${
          mobileView === 'editor' ? 'hidden lg:block' : 'block min-h-screen'
        }`}>
          <PortalPreview
            sections={sections}
            portalData={portalData}
            teamSlug={teamInfo?.url_slug}
          />
        </div>
      </div>
    </div>
  );
}

